import * as React from "react";

import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const alertVariants = cva(
  `relative w-full max-w-content rounded-lg p-6 text-base pl-9
   grid grid-cols-2 gap-6
   before:absolute before:inset-0 before:rounded-l-lg before:bg-blue-700 before:w-1.5 before:h-full
   `,
  {
    variants: {
      variant: {
        default: "bg-blue-200 text-foreground",
        destructive:
          "text-destructive bg-blue-200 [&>svg]:text-current *:data-[slot=alert-description]:text-destructive/90",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        "col-span-2 line-clamp-1 self-end text-xl font-semibold lg:peer-data-[slot=alert-video]:col-span-1",
        className,
      )}
      {...props}
    />
  );
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        "col-span-2 grid content-start gap-1 lg:col-start-1 lg:peer-data-[slot=alert-video]:col-span-1 [&_p]:leading-relaxed",
        className,
      )}
      {...props}
    />
  );
}

function AlertVideo({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-video"
      className={cn(
        "peer relative col-span-2 col-start-1 row-start-3 w-full pt-[56.25%] lg:col-start-2 lg:row-span-2 lg:row-start-1",
        className,
      )}
      {...props}
    />
  );
}

export { Alert, AlertTitle, AlertDescription, AlertVideo };
